/**
 * The engine-state model: ONE state machine describing "is the studio's
 * backend/AI engine available, busy, or blocked right now" — composed from
 * health, auth, entitlements, background jobs, and (per-surface) an AI
 * stream. `hooks/use-engine.ts` is the only consumer that builds the input
 * (it has to call other hooks — useHealth, useAuthGate, useActivity,
 * useEntitlements — which a plain module can't do); this file holds the
 * FRAMEWORK-FREE type and derivation so it's trivially unit-testable and so
 * every feedback component agrees on exactly one shape.
 *
 * There is deliberately no zustand `create()` here: every input already lives
 * in an existing store/query (health, activity, billing) — this module owns
 * no state of its own, only the rule for combining what's already there.
 */

export type EngineState =
  | "unavailable"
  | "initializing"
  | "ready"
  | "processing"
  | "waiting"
  | "partial"
  | "completed"
  | "failed"
  | "needs_auth"
  | "needs_billing"
  | "needs_permission"
  /** A stoppable stream (see `useAiStream().stop()`) that the CALLER stopped
   * deliberately, as opposed to one that errored or simply finished. Only
   * ever produced when a caller reports `stream.stoppedByUser` — nothing
   * derives it on its own (see hooks/use-ai-stream.ts: `stop()` leaves phase
   * `"idle"`, which is indistinguishable from "never started" without the
   * caller telling us which one happened). */
  | "paused";

export type RequiredEntitlement = "models_premium" | "storage_unlimited";

/** A minimal, framework-free view of a background job — a structural subset
 * of `stores/activity.ts`'s `Tracker` so this module doesn't need to import
 * it (any tracker satisfies this). */
export interface EngineJob {
  status: "queued" | "running" | "done" | "failed";
}

export interface EngineStreamSignal {
  phase: "idle" | "streaming" | "done" | "error";
  /** Did the stream produce any output before finishing/erroring? Lets a
   * failed-but-partial generation report `partial` instead of a hard
   * `failed` — there's something on screen worth keeping. */
  hasContent?: boolean;
  /** Set by the caller when `phase` is `"idle"` because THEY called
   * `stop()` — see the `paused` state doc above. */
  stoppedByUser?: boolean;
  /** `useAiStream`'s `meta.error`, when `phase === "error"` — surfaced as
   * `lastError` via `toUserError` (never shown raw). */
  error?: string | null;
}

export interface EngineDerivationInput {
  health: "loading" | "unavailable" | "ok";
  auth: "loading" | "signed-out" | "resolved";
  /** null when this reading doesn't gate on any entitlement. */
  entitlement: { requires: RequiredEntitlement; loading: boolean; satisfied: boolean } | null;
  stream?: EngineStreamSignal;
  /** Truthy when the caller wants a non-stream failure (e.g. a mutation's
   * `onError`) reflected in this reading. */
  hasReportedError?: boolean;
  jobs: EngineJob[];
}

export interface EngineDerivation {
  state: EngineState;
  requiredEntitlement?: RequiredEntitlement;
  /** Which composed input actually produced a `failed`/`partial` state, so
   * the hook layer knows which error to surface as `lastError`. */
  failureSource: "stream" | "reported" | null;
}

/** Pure state machine: given a snapshot of every composed input, decide the
 * single {@link EngineState}. Order matters — earlier checks pre-empt later
 * ones (a dead backend outranks a stray queued job). */
export function deriveEngineState(input: EngineDerivationInput): EngineDerivation {
  const none: EngineDerivation = { state: "ready", failureSource: null };

  if (input.health === "loading") return { ...none, state: "initializing" };
  if (input.health === "unavailable") return { ...none, state: "unavailable" };

  if (input.auth === "loading") return { ...none, state: "initializing" };
  if (input.auth === "signed-out") return { ...none, state: "needs_auth" };

  if (input.entitlement) {
    if (input.entitlement.loading) return { ...none, state: "initializing" };
    if (!input.entitlement.satisfied) {
      return { state: "needs_billing", requiredEntitlement: input.entitlement.requires, failureSource: null };
    }
  }

  if (input.stream?.stoppedByUser) return { ...none, state: "paused" };

  if (input.stream?.phase === "streaming") return { ...none, state: "processing" };

  if (input.stream?.phase === "error") {
    return {
      state: input.stream.hasContent ? "partial" : "failed",
      failureSource: "stream",
    };
  }

  if (input.hasReportedError) return { ...none, state: "failed", failureSource: "reported" };

  const running = input.jobs.some((j) => j.status === "running");
  if (running) return { ...none, state: "processing" };

  const queued = input.jobs.some((j) => j.status === "queued");
  if (queued) return { ...none, state: "waiting" };

  if (input.stream?.phase === "done") return { ...none, state: "completed" };

  return none;
}
